<template>
  <v-container fluid :loading="loading">
    <v-dialog
      v-model="overlays.background.settingsDialog"
      max-width="800"
      transition="slide-x-reverse-transition"
    >
      <device-background-dialog
        :device-id="deviceId"
        @cancel="closeBackgroundDialog"
        @select="setBackground"
      />
    </v-dialog>
    <v-dialog
      v-model="overlays.heatmap.settingsDialog"
      max-width="400"
      transition="slide-x-reverse-transition"
    >
      <v-card>
        <v-toolbar dense elevation="1">
          <v-spacer />
          <v-toolbar-title>
            ヒートマップ
          </v-toolbar-title>
          <v-spacer />
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                @click="overlays.heatmap.settingsDialog = false"
                v-on="on"
                ><v-icon>mdi-close</v-icon></v-btn
              >
            </template>
            <span>閉じる</span>
          </v-tooltip>
        </v-toolbar>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col>
                <v-slider
                  v-model="overlays.heatmap.settings.intensity"
                  :min="overlays.heatmap.settings.min"
                  :max="overlays.heatmap.settings.max"
                  :step="overlays.heatmap.settings.step"
                  label="濃度調整"
                  thumb-label="always"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="overlays.scatter.settingsDialog"
      max-width="400"
      transition="slide-x-reverse-transition"
    >
      <v-card>
        <v-toolbar dense elevation="1">
          <v-spacer />
          <v-toolbar-title>
            フィルター
          </v-toolbar-title>
          <v-spacer />
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                @click="overlays.scatter.settingsDialog = false"
                v-on="on"
                ><v-icon>mdi-close</v-icon></v-btn
              >
            </template>
            <span>閉じる</span>
          </v-tooltip>
        </v-toolbar>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col>
                <v-radio-group
                  label="性別"
                  v-model="overlays.scatter.settings.genders"
                  row
                >
                  <v-radio
                    :value="0"
                    label="両方"
                    on-icon="mdi-gender-male-female"
                  />
                  <v-radio
                    :value="1"
                    label="女性"
                    on-icon="mdi-gender-female"
                  />
                  <v-radio :value="2" label="男性" on-icon="mdi-gender-male" />
                </v-radio-group>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-range-slider
                  v-model="overlays.scatter.settings.ages"
                  label="年齢"
                  min="0"
                  max="100"
                  step="1"
                  thumb-size="24"
                  thumb-label="always"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="overlays.grid.settingsDialog"
      max-width="400"
      transition="slide-x-reverse-transition"
    >
      <v-card>
        <v-toolbar dense elevation="1">
          <v-spacer />
          <v-toolbar-title>
            グリッド
          </v-toolbar-title>
          <v-spacer />
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                @click="overlays.grid.settingsDialog = false"
                v-on="on"
                ><v-icon>mdi-close</v-icon></v-btn
              >
            </template>
            <span>閉じる</span>
          </v-tooltip>
        </v-toolbar>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col>
                <v-checkbox
                  v-model="overlays.grid.settings.guide"
                  label="ガイドライン"
                >
                </v-checkbox>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-slider
                  v-model="overlays.grid.settings.cellNumber"
                  label="セル数"
                  min="5"
                  step="1"
                  max="20"
                  thumb-label="always"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-row justify="end">
      <v-btn color="teal" @click="gridClearCells">
        グリッド クリア
      </v-btn>
      <v-btn color="teal" @click="downloadReport">
        視線データ ダウンロード
      </v-btn>
    </v-row>

    <v-row>
      <v-col cols="auto" lg="3">
        <v-checkbox
          v-for="(overlay, index) in overlayControls"
          :key="index"
          v-model="overlays.states"
          :value="index"
          :label="overlay.text"
          :prepend-icon="overlay.icon"
          @change="overlay.click"
          class="my-n2"
        >
          <template #label>
            <v-tooltip right>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ overlay.text }}</span>
              </template>
              <span>{{ overlay.tooltip }}</span>
            </v-tooltip>
          </template>
          <template #append>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-icon
                  v-on="on"
                  v-if="overlay.settings"
                  @click="overlay.settings"
                  >mdi-cog</v-icon
                >
              </template>
              <span>設定</span>
            </v-tooltip>
          </template>
        </v-checkbox>

        <v-radio-group v-model="overlays.mode">
          <v-radio
            v-for="(mode, index) in overlays.modes"
            :key="index"
            :value="index"
            :label="mode.text"
            :on-icon="mode.icon"
          >
            <template #label>
              <v-tooltip right>
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ mode.text }}</span>
                </template>
                <span>{{ mode.tooltip }}</span>
              </v-tooltip>
            </template>
          </v-radio>
        </v-radio-group>

        <v-list outlined>
          <v-list-item-group
            v-model="overlays.grid.settings.activeArea"
            mandatory
          >
            <v-list-item
              v-for="(area, index) in overlays.grid.settings.areas"
              :key="index"
            >
              <v-list-item-icon>
                <v-btn icon small outlined @click.stop="removeArea(area)">
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title v-text="area.name" />
              </v-list-item-content>
              <v-list-item-avatar :color="area.color" tile>
                <span class="white--text">
                  {{
                    areaHitsInChunk.filter(hit => hit.value === area.value)
                      .length
                  }}
                </span>
              </v-list-item-avatar>
              <v-list-item-avatar :color="area.color" tile>
                <span class="white--text">
                  {{ areaHits.filter(hit => hit.value === area.value).length }}
                </span>
              </v-list-item-avatar>
            </v-list-item>
          </v-list-item-group>
          <v-list-item>
            <v-list-item-content>
              <v-btn text @click="addNewArea">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-responsive :aspect-ratio="4 / 3">
          <v-card
            outlined
            ref="overlayContainer"
            width="640"
            height="480"
            v-resize="overlayResized"
          >
            <apex-chart
              ref="chart"
              :series="chartSeries"
              :options="chartOptions"
              height="100%"
              type="scatter"
            />
            <v-overlay
              :value="overlays.background.enabled"
              opacity="0"
              absolute
            >
              <svg :width="overlay.width" :height="overlay.height">
                <image
                  :href="overlays.background.settings.src"
                  :width="overlay.width"
                  :height="overlay.height"
                  class="draggable"
                  @load="makeDraggableImage"
                  opacity="0.5"
                />
              </svg>
            </v-overlay>
            <v-overlay :value="overlays.heatmap.enabled" opacity="0.1" absolute>
              <heatmapjs-vue
                class="heatmapjs-container"
                :min="overlays.heatmap.settings.min"
                :max="overlays.heatmap.settings.max"
                :data="heatmapPoints"
              />
            </v-overlay>
            <v-overlay :value="overlays.grid.enabled" opacity="0.1" absolute>
              <svg
                class="cartesian"
                :width="overlay.width"
                :height="overlay.height"
                viewBox="-320 -240 640 480"
              >
                <defs>
                  <pattern
                    x="-320"
                    y="-240"
                    id="grid"
                    :width="gridCellSize.width"
                    :height="gridCellSize.height"
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      :width="gridCellSize.width"
                      :height="gridCellSize.height"
                      stroke="white"
                      stroke-width="1"
                      fill="transparent"
                      fill-opacity="0.0"
                    />
                  </pattern>
                  <pattern
                    x="-320"
                    y="-240"
                    id="cells"
                    :width="gridCellSize.width"
                    :height="gridCellSize.height"
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      :width="gridCellSize.width"
                      :height="gridCellSize.height"
                      stroke-width="0"
                      fill="transparent"
                      fill-opacity="0.0"
                    />
                  </pattern>
                </defs>
                <rect
                  x="-320"
                  y="-240"
                  id="gridGuide"
                  width="100%"
                  height="100%"
                  fill="url(#grid)"
                />
                <rect
                  x="-320"
                  y="-240"
                  id="gridCells"
                  width="100%"
                  height="100%"
                  fill="url(#cells)"
                  @click="gridAddCell"
                />
              </svg>
            </v-overlay>
          </v-card>
          <v-card-actions>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="seekAnimation(false)"
                  v-on="on"
                  x-small
                  color="teal"
                  fab
                >
                  <v-icon>mdi-light mdi-skip-previous</v-icon>
                </v-btn>
              </template>
              <span>前</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="playAnimation(300)"
                  v-on="on"
                  color="teal"
                  small
                  fab
                >
                  <v-icon v-if="isPlaying">mdi-light mdi-pause</v-icon>
                  <v-icon v-else>mdi-light mdi-play</v-icon>
                </v-btn>
              </template>
              <span v-if="isPlaying">Pause</span>
              <span v-else>再生</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="seekAnimation(true)"
                  v-on="on"
                  x-small
                  color="teal"
                  fab
                >
                  <v-icon>mdi-light mdi-skip-next</v-icon>
                </v-btn>
              </template>
              <span>次</span>
            </v-tooltip>
            <v-slider
              v-model="currentStep"
              :step="animationStep"
              :max="animationSteps"
              ticks="always"
              tick-size="4"
              thumb-label="always"
              class="mt-4"
              color="teal"
              track-color="grey"
              loader-height="4"
            />
          </v-card-actions>
        </v-responsive>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import KalmanFilter from 'kalmanjs'
import apexChart from 'vue-apexcharts'
import { mapActions } from 'vuex'
import { Point, SVG } from '@svgdotjs/svg.js'
import { flatten, flattenDeep } from 'lodash/core'
import { zip } from 'lodash/array'
import { Pick } from '@/protocol'
import DeviceBackgroundDialog from '@/components/device/DeviceBackgroundDialog'
import { downloadFunctionsMixin } from '@/plugins/mixins'
import {
  getDevice,
  getReportDetailsWithin
} from '@/firebaseAccess/firestoreAccess'

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

const mapRange = (value, x1, y1, x2, y2) =>
  ((value - x1) * (y2 - x2)) / (y1 - x1) + x2

export default {
  name: 'HeatMapChart',
  props: {
    loading: {
      type: Boolean,
      required: false
    }
  },
  mixins: [downloadFunctionsMixin],
  components: { apexChart, DeviceBackgroundDialog },
  data() {
    return {
      overlays: {
        // scatter plots start enabled by default
        states: [0, 1],
        mode: 0,
        modes: [
          { text: '時系列', tooltip: '時系列', icon: 'mdi-eye' },
          {
            text: '全視点表示（一人分）',
            tooltip: '全視点表示（一人分）',
            icon: 'mdi-walk'
          }
        ],
        scatter: {
          series: { name: '位置' },
          enabled: true,
          settingsDialog: false,
          settings: {
            genders: 0,
            ages: [0, 100]
          }
        },
        calibration: {
          series: { name: '較正' },
          enabled: true
        },
        background: {
          enabled: false,
          settingsDialog: false,
          settings: {
            src: ''
          }
        },
        heatmap: {
          enabled: false,
          settingsDialog: false,
          settings: {
            min: 0.1,
            max: 2,
            step: 0.1,
            intensity: 1.0
          }
        },
        grid: {
          enabled: false,
          settingsDialog: false,
          settings: {
            guide: true,
            activeArea: 0,
            cellNumber: 10,
            areas: [{ value: 1, name: 'エリア 1', color: 'green' }],
            colors: [
              '#9C27b0',
              '#00CAE3',
              '#4CAF50',
              '#ff9800',
              '#E91E63',
              '#FF5252'
            ]
          }
        }
      },
      overlayControls: [
        {
          text: '視線データ',
          tooltip: '視線データ',
          icon: 'mdi-scatter-plot',
          click: () => {
            this.overlays.scatter.enabled = !this.overlays.scatter.enabled
            if (this.chartSeries.length > 0) {
              this.$refs.chart.toggleSeries(this.overlays.scatter.series.name)
            }
          }
          // settings: () => {
          //   this.overlays.scatter.settingsDialog = !this.overlays.scatter
          //     .settingsDialog
          // }
        },
        {
          text: 'ターゲット',
          tooltip: 'ターゲット',
          icon: 'mdi-target',
          click: () => {
            this.overlays.calibration.enabled = !this.overlays.calibration
              .enabled
            if (this.chartSeries.length > 0) {
              this.$refs.chart.toggleSeries(
                this.overlays.calibration.series.name
              )
            }
          }
        },
        {
          text: '背景画像',
          tooltip: '背景画像',
          icon: 'mdi-image',
          click: () => {
            if (!this.overlays.background.settings.src) {
              this.overlays.background.settingsDialog = true
            } else {
              this.overlays.background.enabled = !this.overlays.background
                .enabled
            }
          },
          settings: () => {
            this.overlays.background.settingsDialog = true
          }
        },
        {
          text: 'ヒートマップ',
          tooltip: 'ヒートマップ',
          icon: 'mdi-chart-tree',
          click: () => {
            this.overlays.heatmap.enabled = !this.overlays.heatmap.enabled
          },
          settings: () => {
            this.overlays.heatmap.settingsDialog = !this.overlays.heatmap
              .settingsDialog
          }
        },
        {
          text: 'グリッド',
          tooltip: 'グリッド',
          icon: 'mdi-view-grid-plus-outline',
          click: () => {
            this.overlays.grid.enabled = !this.overlays.grid.enabled
            if (!this.overlays.grid.enabled) {
              this.gridClearCells()
            }
          },
          settings: () => {
            this.overlays.grid.settingsDialog = !this.overlays.grid
              .settingsDialog
          }
        }
      ],
      chartOptions: {
        chart: {
          type: 'scatter',
          zoom: {
            enabled: false
          },
          animations: {
            enabled: false
          },
          toolbar: {
            offsetX: 0
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
        grid: {
          padding: {
            top: -30,
            left: 0,
            right: 0,
            bottom: -14
          },
          xaxis: {
            lines: {
              show: false
            }
          },
          yaxis: {
            lines: {
              show: false
            }
          }
        },
        tooltip: {
          enabled: true,
          x: {
            show: true,
            formatter: value => {
              return Math.round(value) + 'mm'
            }
          },
          y: {
            show: true,
            formatter: value => {
              return Math.round(value) + 'mm'
            }
          }
        },
        xaxis: {
          min: -2000,
          max: 2000,
          show: false,
          crosshairs: {
            show: true
          },
          labels: {
            show: false
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          },
          tooltip: {
            enabled: false
          }
        },
        yaxis: {
          min: -1000,
          max: 1000,
          show: false,
          crosshairs: {
            show: true
          },
          labels: {
            show: false
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          },
          tooltip: {
            enabled: false
          }
        }
      },

      // default chart series
      chartSeries: [
        {
          data: [
            [0, 0],
            [-2000, 1000],
            [-2000, -1000],
            [2000, 1000],
            [2000, -1000]
          ]
        }
      ],

      grid: {
        cells: []
      },

      overlay: {
        width: undefined,
        height: undefined
      },

      isPlaying: false,
      currentStep: 0,

      deviceId: undefined,
      device: undefined,
      events: []
    }
  },
  computed: {
    mode() {
      return this.overlays.mode
    },
    gridCells() {
      return this.grid.cells
    },
    gridGuideVisible() {
      return this.overlays.grid.settings.guide
    },
    gridSelectedArea() {
      return this.overlays.grid.settings.areas[
        this.overlays.grid.settings.activeArea
      ]
    },
    gridCellNumber() {
      return this.overlays.grid.settings.cellNumber
    },
    gridCellSize() {
      return {
        width: this.overlay.width / this.gridCellNumber,
        height: this.overlay.height / this.gridCellNumber
      }
    },
    animationStep() {
      return 1
    },
    animationSteps() {
      // playback slider starts from zero, max steps adjusted to match array index
      if (this.chunkedPoints.length === 0) {
        return 0
      }
      return this.chunkedPoints.length - 1
    },
    calibrationPoints() {
      if (this.device && this.device.config && this.device.config.calibration) {
        return this.device.config.calibration.regions.map(r => [
          r.center.x,
          r.center.y
        ])
      }
      return []
    },
    filteredPoints() {
      // filter data according to scatter settings
      if (this.events.length > 0) {
        // const ageFilter = this.overlays.scatter.settings.ages
        // const genderFilter = this.overlays.scatter.settings.genders
        // return this.events.filter(
        //   e =>
        //     ageFilter[0] <= e.age &&
        //     e.age <= ageFilter[1] &&
        //     (genderFilter === 0 || genderFilter === Number(e.gender))
        // )
        return this.events
      }
      return []
    },
    heatmapPoints() {
      // heatmap is not currently operated by the animation playback, it is a static representation of all events
      // it is possible to make dynamic by flattening either all chunks or currentChunk instead
      if (this.filteredPoints.length > 0) {
        return flattenDeep(
          this.filteredPoints.map(e =>
            e.data.map(d =>
              d.flow.map(f => {
                return {
                  x: mapRange(
                    Math.round(f.x),
                    this.chartOptions.xaxis.min,
                    this.chartOptions.xaxis.max,
                    0,
                    this.overlay.width
                  ),
                  y: mapRange(
                    Math.round(f.y),
                    this.chartOptions.yaxis.min,
                    this.chartOptions.yaxis.max,
                    0,
                    this.overlay.height
                  ),
                  value: this.overlays.heatmap.settings.intensity
                }
              })
            )
          )
        )
      }
      return []
    },
    chunkedPoints() {
      if (this.filteredPoints.length > 0) {
        // we want to zip the gaze flows, so first array contains first
        // positions where people looked, second contains only second points etc.
        const flows = flatten(
          this.filteredPoints.map(e =>
            e.data.map(d => d.flow.map(f => [f.x, f.y]))
          )
        )

        if (this.overlays.mode) {
          return flows
        } else {
          // note: arrays are padded with undefined during the zip operation
          return zip(...flows)
        }
      }
      return []
    },
    currentChunk() {
      // apex charts doesn't cope with undefined points, replace them with [null, null]
      // we can also use _.compact here, but it changes the array shape, which might not be desirable
      if (this.chunkedPoints.length > 0) {
        return this.chunkedPoints[this.currentStep].map(c =>
          c !== undefined ? c : [null, null]
        )
      }
      return []
    },
    areaHits() {
      return this.areaHitsInPoints(this.gridCells, flatten(this.chunkedPoints))
    },
    areaHitsInChunk() {
      return this.areaHitsInPoints(this.gridCells, this.currentChunk)
    }
  },
  methods: {
    ...mapActions({
      showSnack: 'snackbar/showSnack'
    }),
    downloadReport() {
      // aggregate the cell hits per event, this represents individual movement pattern between cells
      const contents = []

      for (const report of this.events) {
        for (const event of report.data) {
          if (event.flow.length === 0) {
            continue
          }

          const hits = this.areaHitsInPoints(
            this.gridCells,
            event.flow.map(f => [f.x, f.y]),
            true
          )
          // first pass filter: reduce noise in the points using kalman filter
          const kf = new KalmanFilter({ R: 0.01, Q: 0.05 })
          const filtered = hits
            .map(flow => flow.value)
            .map(value => Math.round(kf.filter(value)))

          // second pass filter: count N consecutive values and tally up the time per target
          const areas = []
          const consecutive = []
          // todo: tidy up this mess and handle corner cases
          if (filtered.length > 2) {
            let start = 0
            let count = 0
            let last = null
            for (const [index, value] of filtered.entries()) {
              if (last === value) {
                count += 1
              } else {
                if (count >= 3) {
                  consecutive.push({
                    value: last,
                    start: start,
                    stop: start + count
                  })
                }
                count = 1
                start = index
                last = value
              }
            }
            // push the last value if it was consecutive while the loop has finished
            if (count >= 3) {
              consecutive.push({
                value: last,
                start: start,
                stop: start + count
              })
            }

            console.log(event.duration)

            let durationMilli = 0
            if (event.duration.seconds !== undefined) {
              durationMilli += event.duration.seconds * 1000
            }
            if (event.duration.nanos !== undefined) {
              durationMilli += event.duration.nanos / 1e6
            }

            let lastIndex = 0
            const pointDuration = durationMilli / event.flow.length
            for (const area of consecutive) {
              // pad with empty area between consecutive
              if (area.start !== lastIndex) {
                areas.push({
                  value: 0,
                  duration: Math.round((area.start - lastIndex) * pointDuration)
                })
              }
              areas.push({
                value: area.value,
                duration: Math.round((area.stop - area.start) * pointDuration)
              })
              lastIndex = area.stop
            }

            // add up the trailing non-consecutive as empty
            if (lastIndex < filtered.length) {
              if (areas.length > 0) {
                const duration = Math.round(
                  (filtered.length - lastIndex) * pointDuration
                )
                const last = areas[areas.length - 1]
                if (last.value === 0) {
                  last.duration += duration
                } else {
                  areas.push({
                    value: 0,
                    duration: duration
                  })
                }
              }
            }
          }

          // pick up area names from the grid settings
          const flow = areas.map(a => {
            if (a.value === 0) return { value: a.value, duration: a.duration }
            const area = this.overlays.grid.settings.areas.find(
              t => t.value === a.value
            )
            return {
              value: area.name,
              duration: a.duration
            }
          })

          const timestamp = new Date(1970, 0, 1)
          timestamp.setSeconds(event.timestamp.seconds)

          contents.push({
            uid: event.uid,
            timestamp: timestamp,
            flow: flow
            // gender: event.gender,
            // age: event.age,
          })
        }
      }

      const anchor = this.downloadFile(
        'export.json',
        'data:text/json;charset=utf-8,' + JSON.stringify(contents)
      )
      anchor.remove()
    },
    areaHitsInPoints(cells, points, withNoHit = false) {
      const svg = SVG('#gridCells')
      const scaleWidth =
        (Math.abs(this.chartOptions.xaxis.min) +
          Math.abs(this.chartOptions.xaxis.max)) /
        this.overlay.width
      const scaleHeight =
        (Math.abs(this.chartOptions.yaxis.min) +
          Math.abs(this.chartOptions.yaxis.max)) /
        this.overlay.height

      // to determine the relative rect position, rbox (aka getBoundingClientRect) is used
      // and points in the chunk that overlap with rect bounding box, are counted towards that cell
      const hits = []

      for (const point of points) {
        // ignore null points, as they are simply padding to keep chunks the same size
        // if (point === undefined || point.some(p => p === null)) continue
        if (point === undefined) continue

        let hit = false
        for (const cell of cells) {
          // cant apply .scale here, because it modifies the object, neither can .clone rect or a box
          const box = cell.rect.rbox(svg)
          const xScaled = box.x * scaleWidth
          const yScaled = box.y * scaleHeight
          const wScaled = box.width * scaleWidth
          const hScaled = box.height * scaleHeight

          // check if flow point is located inside this cell
          if (
            xScaled <= point[0] &&
            point[0] <= xScaled + wScaled &&
            yScaled <= point[1] &&
            point[1] <= yScaled + hScaled
          ) {
            hits.push({ value: cell.value, name: cell.name, point: point })
            hit = true
            break
          }
        }

        // no hit points are used during data export, representing a point that did not overlap any areas
        if (!hit && withNoHit) {
          hits.push({ value: 0, name: null, point: point })
        }
      }

      return hits
    },
    makeDraggableImage(evt) {
      // const image = evt.target
      const svg = evt.target.parentNode
      svg.addEventListener('mousedown', startDrag)
      svg.addEventListener('mousemove', drag)
      svg.addEventListener('mouseup', endDrag)
      svg.addEventListener('mouseleave', endDrag)
      svg.addEventListener('touchstart', startDrag)
      svg.addEventListener('touchmove', drag)
      svg.addEventListener('touchend', endDrag)
      svg.addEventListener('touchleave', endDrag)
      svg.addEventListener('touchcancel', endDrag)
      svg.addEventListener('mousewheel', startZoom)

      const scaleFactor = 1.25

      let element,
        offset,
        transform = null

      function getMousePosition(evt) {
        const ctm = svg.getScreenCTM()
        if (evt.touches) {
          evt = evt.touches[0]
        }
        return {
          x: (evt.clientX - ctm.e) / ctm.a,
          y: (evt.clientY - ctm.f) / ctm.d
        }
      }

      function makeTransform(target) {
        const transforms = target.transform.baseVal
        if (
          transforms.length === 0 ||
          transforms.getItem(0).type !== SVGTransform.SVG_TRANSFORM_TRANSLATE
        ) {
          // create an empty transform
          const translate = svg.createSVGTransform()
          translate.setTranslate(0, 0)
          target.transform.baseVal.insertItemBefore(translate, 0)
        }
        return transforms.getItem(0)
      }

      function startDrag(evt) {
        if (evt.target.classList.contains('draggable')) {
          element = evt.target
          offset = getMousePosition(evt)
          transform = makeTransform(element)
          offset.x -= transform.matrix.e
          offset.y -= transform.matrix.f
        }
      }
      function drag(evt) {
        if (element) {
          evt.preventDefault()
          const position = getMousePosition(evt)
          transform.setTranslate(position.x - offset.x, position.y - offset.y)
        }
      }
      function endDrag() {
        element = null
      }
      function startZoom(evt) {
        if (evt.shiftKey) {
          if (evt.target.classList.contains('draggable')) {
            evt.preventDefault()

            let normalized
            let delta = evt.wheelDelta
            if (delta) {
              normalized = delta % 120 === 0 ? delta / 120 : delta / 12
            } else {
              delta = evt.deltaY || evt.detail || 0
              normalized = -(delta % 3 ? delta * 10 : delta / 3)
            }
            const scaleDelta = normalized > 0 ? 1 / scaleFactor : scaleFactor

            const trans = makeTransform(evt.target)
            trans.setScale(scaleDelta, scaleDelta)
          }
        }
      }
    },
    overlayResized() {
      // keep the size of the container, it is used to scale other overlays
      this.overlay.width = this.$refs.overlayContainer.$el.clientWidth
      this.overlay.height = this.$refs.overlayContainer.$el.clientHeight
    },
    gridRemoveCell(event) {
      // event.target is the reference to an area node that we can search
      // remove the cell regardless of which target is selected
      const cell = this.grid.cells.find(c => c.rect.node === event.target)
      if (cell) {
        cell.rect.remove()
        const index = this.grid.cells.indexOf(cell)
        this.grid.cells.splice(index, 1)
      }
    },
    gridClearCells() {
      this.grid.cells.forEach(c => c.rect.remove())
      this.grid.cells = []
    },
    gridAddCell(event) {
      // draw a new svg rect, filling the grid where the user clicked
      const svg = SVG(event.target.ownerSVGElement)
      const screen = svg.screenCTM().inverse()
      const point = new Point(event.clientX, event.clientY).transform(screen)

      const width = this.gridCellSize.width
      const height = this.gridCellSize.height
      const target = this.gridSelectedArea

      const rect = svg
        .rect(width, height)
        .move(
          Math.floor(point.x / width) * width,
          Math.floor(point.y / height) * height
        )
        .fill({ color: target.color, opacity: 0.5 })
        .click(this.gridRemoveCell)

      this.grid.cells.push({
        value: target.value,
        name: target.name,
        rect: rect
      })
    },
    addNewArea() {
      const index = this.overlays.grid.settings.areas.length + 1
      const color = this.overlays.grid.settings.colors[
        Math.floor(
          Math.random() *
            Math.floor(this.overlays.grid.settings.colors.length - 1)
        )
      ]
      const newArea = {
        value: index,
        name: 'エリア ' + index,
        color: color
      }

      this.overlays.grid.settings.areas.push(newArea)
    },
    removeArea(area) {
      this.overlays.grid.settings.areas.splice(
        this.overlays.grid.settings.areas.indexOf(area),
        1
      )
      // remove areas from grid
      const cells = this.grid.cells.filter(c => c.value === area.value)
      cells.forEach(c => c.rect.remove())
    },
    closeBackgroundDialog() {
      this.overlays.background.settingsDialog = false
      if (!this.overlays.background.settings.src) {
        // flick the background button to off
        if (this.overlays.states.includes(1)) {
          this.overlays.states.splice(this.overlays.states.indexOf(1), 1)
        }
      }
    },
    setBackground(src) {
      this.overlays.background.settings.src = src
      this.overlays.background.settingsDialog = false
      // flick the overlay button to on
      if (!this.overlays.states.includes(2)) {
        this.overlays.states.push(2)
      }
      this.overlays.background.enabled = true
    },

    seekAnimation(forward) {
      if (forward) {
        if (this.currentStep < this.animationSteps) {
          this.currentStep += this.animationStep
        } else {
          this.currentStep = 0
        }
      } else {
        if (this.currentStep === 0) {
          this.currentStep = this.animationSteps
        } else {
          this.currentStep -= 1
        }
      }
    },
    async playAnimation(delay) {
      this.isPlaying = !this.isPlaying
      while (this.isPlaying) {
        if (this.currentStep < this.animationSteps) {
          this.currentStep += this.animationStep
        } else {
          this.currentStep = 0
        }
        await wait(delay)
      }
    },
    stopAnimation() {
      // this will also force currentChunk to be updated, triggering series update
      this.isPlaying = false
      this.currentStep = 0
    },
    async updateChart(deviceId, startDate, endDate) {
      if (deviceId && startDate && endDate) {
        this.deviceId = deviceId
        this.device = (await getDevice(deviceId)).data()

        const reportRefs = await getReportDetailsWithin(
          deviceId,
          Pick.Report.ReportInfo.Type.TYPE_GAZE,
          startDate,
          endDate
        )

        if (reportRefs.size > 0) {
          // todo: we should probably paginate these according to the slider
          this.events = reportRefs.docs.map(d => d.data())
        }
      }
    }
  },
  updated() {
    // recalculate overlay size upon updating to keep the grid sizes in sync
    this.$nextTick().then(() => {
      this.overlayResized()
    })
  },
  watch: {
    filtered() {
      this.stopAnimation()
    },
    mode() {
      this.stopAnimation()
    },
    currentChunk() {
      if (this.currentChunk.length > 0) {
        this.chartSeries = [
          { name: '位置', data: this.currentChunk },
          {
            name: this.overlays.calibration.series.name,
            data: this.calibrationPoints
          }
        ]
      }
    },
    gridGuideVisible() {
      const guide = SVG('#gridGuide')
      if (guide) {
        if (this.gridGuideVisible) {
          guide.show()
        } else {
          guide.hide()
        }
      }
    }
  }
}
</script>

<style>
/* Flip SVG across Y axis, instead of reversing the chart Y axis */
/* Combined with negative starting XY points, this turns SVG into a cartesian coord system with center at 0,0 */
svg.cartesian {
  transform: scaleY(-1);
}

.draggable {
  cursor: move;
}

.heatmapjs-container {
  width: 100%;
  height: 100%;
  min-width: 640px;
  min-height: 480px;
  transform: scaleY(-1);
}
</style>
